import { Input } from '@/components/ui/input';
import { COUNTRIES } from '@/lib/countries';
import { cn } from '@/lib/utils';
import {
  PLACE_QUERY_MAX,
  type CollectionPlaceCopy,
  type CollectionPlaceErrorKey,
  type DeclaredPlaceHistoryItem,
  type Place,
} from '../collection-place.contract';
import type { CountryData } from '@/lib/countries';

export type CitySearchState = 'idle' | 'loading' | 'empty' | 'error' | 'ready';

type CityPickerProps = {
  copy: CollectionPlaceCopy;
  query: string;
  state: CitySearchState;
  errorKey: CollectionPlaceErrorKey;
  results: Place[];
  previousPlaces: DeclaredPlaceHistoryItem[];
  isLoadingPreviousPlaces: boolean;
  countryCode: string;
  language: 'fr' | 'en';
  onQueryChange: (query: string) => void;
  onCountryChange: (countryCode: string) => void;
  onSelect: (place: Place) => void;
  onUseTyped: (country: CountryData) => void;
};

export function CityPicker({ copy, query, state, errorKey, results, previousPlaces, isLoadingPreviousPlaces, countryCode, language, onQueryChange, onCountryChange, onSelect, onUseTyped }: CityPickerProps) {
  const countryNames = new Intl.DisplayNames([language], { type: 'region' });
  const canUseTyped = query.trim().length > 0 && query.trim().length <= 120;
  return (
    <div className="space-y-2">
      <label className="block space-y-1.5">
        <span className="text-xs font-semibold uppercase tracking-[0.18em] text-muted-foreground/70">
          {copy.searchLabel}
        </span>
        <Input
          type="search"
          autoComplete="off"
          maxLength={PLACE_QUERY_MAX}
          value={query}
          placeholder={copy.searchPlaceholder}
          onChange={(event) => onQueryChange(event.target.value)}
          className="h-11 rounded-2xl border-border/45 bg-muted/[0.08]"
          data-testid="collection-place-search"
        />
      </label>

      {state === 'loading' && <p className="px-1 text-xs text-muted-foreground">{copy.searching}</p>}
      {state === 'empty' && <p className="px-1 text-xs text-muted-foreground">{copy.noResult}</p>}
      {state === 'error' && <p className="px-1 text-xs text-destructive">{copy[errorKey]}</p>}

      {(state === 'empty' || state === 'error') && isLoadingPreviousPlaces && (
        <p role="status" className="px-1 text-xs text-muted-foreground">{copy.previousPlacesLoading}</p>
      )}
      {(state === 'empty' || state === 'error') && previousPlaces.length > 0 && (
        <div className="space-y-1.5">
          <p className="px-1 text-xs font-medium text-muted-foreground">{copy.previousPlaces}</p>
          {previousPlaces.map((place) => (
            <button
              key={place.placeId}
              type="button"
              onClick={() => onSelect({
                placeId: place.placeId,
                city: place.city,
                region: place.region,
                countryCode: place.countryCode,
                countryName: place.countryName,
              })}
              className="flex w-full items-center justify-between rounded-xl border border-border/40 px-3 py-2 text-left hover:border-primary/30 hover:bg-primary/[0.05]"
              data-testid="collection-place-history-item"
            >
              <span className="text-sm font-semibold text-foreground">{place.city}</span>
              <span className="text-xs text-muted-foreground">{place.countryName}</span>
            </button>
          ))}
        </div>
      )}

      {canUseTyped && state !== 'loading' && (
        <div className="space-y-2 rounded-xl border border-border/45 p-3">
          <label className="block space-y-1.5">
            <span className="text-xs font-medium text-muted-foreground">{copy.countryLabel}</span>
            <select
              value={countryCode}
              onChange={(event) => onCountryChange(event.target.value)}
              className="h-10 w-full rounded-xl border border-border/45 bg-background px-3 text-sm text-foreground"
              data-testid="collection-place-country"
            >
              <option value="">{copy.chooseCountry}</option>
              {COUNTRIES.map((country) => (
                <option key={country.iso} value={country.iso}>
                  {countryNames.of(country.iso) ?? country.name}
                </option>
              ))}
            </select>
          </label>
          <button
            type="button"
            disabled={!countryCode}
            onClick={() => {
              const country = COUNTRIES.find((option) => option.iso === countryCode);
              if (country) onUseTyped(country);
            }}
            className="w-full rounded-xl bg-primary/10 px-3 py-2 text-left text-sm font-semibold text-primary enabled:hover:bg-primary/15 disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="collection-place-use-typed"
          >
            {copy.useTypedPlace}: “{query.trim()}”
          </button>
        </div>
      )}

      {state === 'ready' && (
        <ul role="listbox" aria-label={copy.searchLabel} className="max-h-60 space-y-1 overflow-y-auto pr-1">
          {results.map((place) => (
            <li key={place.placeId}>
              <button
                type="button"
                role="option"
                aria-selected={false}
                onClick={() => onSelect(place)}
                className={cn(
                  'flex w-full flex-col items-start rounded-xl border border-border/40 px-3 py-2 text-left transition-colors',
                  'hover:border-primary/30 hover:bg-primary/[0.05]',
                )}
              >
                <span className="text-sm font-semibold text-foreground">{place.city}</span>
                <span className="text-xs text-muted-foreground">
                  {[place.region, place.countryName].filter(Boolean).join(', ')}
                </span>
              </button>
            </li>
          ))}
        </ul>
      )}

      <p className="px-1 text-[0.65rem] text-muted-foreground/60">
        {copy.attribution}{' · '}
        <a href="https://www.geoapify.com/" target="_blank" rel="noreferrer" className="underline underline-offset-2">
          Geoapify
        </a>
      </p>
    </div>
  );
}
